import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/lib/UI/loadingSpinner/LoadingSpinner';
import {
  Box,
  Button,
  Divider,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useAppSelector } from 'setup/store';
import { useAccessActions } from 'actions/access-actions';
import { useDayReportEntryActions } from 'actions/day-report-entry-actions';
import { IDayReportField, IPageData, SnackbarType } from 'common/interfaces';
import {
  IDayReport,
  IDayReportLineItem,
  IDayReportVoidLineItem,
  IReportExportData,
} from 'common/interfaces/iDayReports';
import UITable, { RowItem } from 'components/lib/UI/table/UITable';
import { IStore } from 'common/interfaces/istore';
import { useNavigate, useParams } from 'react-router-dom';
import { isArray } from 'lodash';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { getPreSignedDocument } from 'services/document-service';
import { IGasReport } from 'common/interfaces/iGasReport';
import { useGasReportEntryActions } from 'actions/gas-report-actions';
import { formatDecimalNumber } from 'common/utils';
import { useStoreActions } from 'actions/store-actions';
import UICard from 'components/lib/card/UICard';
import { IShiftReportReview } from 'common/interfaces/ishiftReport';
import AccessKeys from 'common/constants/acces';
import { DATE_FORMATS, ShiftReportStatusEnum } from 'common/constants';
import { useAppActions } from 'actions/app-actions';
import { convertToFieldNameMetaMap, getDayReportDisplayDate } from 'common/utils/day-report';
import { getReportDisplayDate } from 'common/utils/shift-report';
import useGlobalStyles from 'assets/styles/global-styles';
import { formatCurrency } from 'common/utils/string-utils';
import { getDisplayDate } from 'common/utils/date-util';
import { ReportCategoryType } from 'common/constants/features';

const FORM_ITEM_CODES_GAS = [
  'regularGallons',
  'regularSales',
  'regularVoids',
  'regularNetSales',

  'plusGallons',
  'plusSales',
  'plusVoids',
  'plusNetSales',

  'premiumGallons',
  'premiumSales',
  'premiumVoids',
  'premiumNetSales',

  'dieselGallons',
  'dieselSales',
  'dieselVoids',
  'dieselNetSales',
  'paidOuts',
  'discounts',

  'totalSales',

  'salesCCVisaMC',
  'salesDC',
  'salesCCOther',
  'salesOther',
  'totalCCSales',
  'netGasDeposit',

  'totalSalesGas',
  'stateTaxGas',
  'distTaxGas',
  'totalTaxGas',
  'totalSalesWithoutTaxGas',

  'totalSalesDsl',
  'stateTaxDsl',
  'distTaxDsl',
  'totalTaxDsl',
  'totalSalesWithoutTaxDsl',

  'totalSales',
  'totalSalesWithoutTax',
];
const BLANK_LINES_AFTER = [
  'regularNetSales',
  'plusNetSales',
  'premiumNetSales',
  'discounts',
  'totalSales',
  'netGasDeposit',
  'totalSalesWithoutTaxGas',
  'totalSalesWithoutTaxDsl',
];
const BLANK_LINES_AFTER_SALES = ['taxableOthers', 'crvNonTaxable'];
const BOLD_ITEMS = [
  'regularNetSales',
  'plusNetSales',
  'premiumNetSales',
  'dieselNetSales',
  'totalSales',
  'totalCCSales',
  'netGasDeposit',
  'totalSalesWithoutTax',
  'totalCashRecv',
  'netTotalSales',
  'netTotalSales',
  'totalCashPO',
  'totalDeposit',
  'totalCashOver',
  'taxable',
  'totalSalesTaxCollected',
];

const BOLD_ITEMS_SUMMARY_TABLE = ['totalCashOver'];
export const MISC_ITEMS_ARRAY = 'miscItemsArr';
export const MISC_ITEMS_CODE = 'miscItem';

const COLOR_ARRAY = ['#785297', '#21B58C', '#FB7F60', '#21B58C', '#fd8d3c'];

const DayReportDetails = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const reportId = params.reportId ? Number(params.reportId) : undefined;
  const appConfig = useAppSelector((state) => state.appReducer.appConfig);

  const error = useAppSelector((state) => state.dayReportReducer.error);
  const isLoading = useAppSelector((state) => state.dayReportReducer.loading);
  const loadingStatus = useAppSelector((state) => state.dayReportReducer.loadingStatus);

  const [salesData, setSalesData] = useState<IPageData<IDayReportLineItem>>();
  const [cashPOData, setCashPOData] = useState<IPageData<IDayReportLineItem>>();
  const [cashDepositData, setCashDepositData] = useState<IPageData<IDayReportLineItem>>();
  const [voidData, setVoidData] = useState<IPageData<IDayReportVoidLineItem>>();
  const [summaryTableData, setSummaryTableData] = useState<IPageData<IDayReportLineItem>>();
  const [summaryData, setSummaryData] = useState<IDayReport | undefined>({} as IDayReport);
  const [gasReportData, setGasReportData] = useState<IPageData<IDayReportLineItem>>();
  const [comments, setComments] = useState<string>('');
  const [openReviewForm, setOpenReviewForm] = useState<boolean>(false);
  const [reviews, setReviews] = useState<IPageData<IShiftReportReview>>();
  const [reviewError, setReviewError] = useState<string>();
  const dayReportCategoryWiseFields = useAppSelector((state) => state.sessionReducer.dayReportCategoryWiseFields);
  const [dayReportFieldsSalesEntry, setDayReportFieldsSalesEntry] = useState<{ [itemCode: string]: IDayReportField }>();
  const [dayReportFieldsPOEntry, setDayReportFieldsPOEntry] = useState<{ [itemCode: string]: IDayReportField }>();
  const [dayReportFieldsDepositEntry, setDayReportFieldsDepositEntry] = useState<{
    [itemCode: string]: IDayReportField;
  }>();
  const [dayReportFieldsGasSalesEntry, setDayReportFieldsGasSalesEntry] = useState<{
    [itemCode: string]: IDayReportField;
  }>();

  const [storeData, setStoreData] = useState<IStore>();

  const { hasAccess } = useAccessActions();
  const { showSnackBar } = useAppActions();
  const { approveDayReport, loadDayReportById, loadDayReportEntry, loadDayReportReviews, saveDayReportReviews } =
    useDayReportEntryActions();
  const { loadGasReportEntry } = useGasReportEntryActions();
  const { loadStoreById } = useStoreActions();

  const globalStyles = useGlobalStyles();

  const [preSignedUrlMap, setPreSignedUrlMap] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    if (dayReportCategoryWiseFields) {
      const salesFieldMap = convertToFieldNameMetaMap(
        dayReportCategoryWiseFields.fields[ReportCategoryType.CATEGORY_SALES_ENTRY],
      );
      const poFieldMap = convertToFieldNameMetaMap(
        dayReportCategoryWiseFields.fields[ReportCategoryType.CATEGORY_CASH_PO_ENTRY],
      );
      const depositFieldMap = convertToFieldNameMetaMap(
        dayReportCategoryWiseFields.fields[ReportCategoryType.CATEGORY_CASH_DEPOSIT_ENTRY],
      );
      const gasSalesFieldMap = convertToFieldNameMetaMap(
        dayReportCategoryWiseFields.fields[ReportCategoryType.CATEGORY_GAS_SALES_ENTRY],
      );
      console.log('Debug::salesFieldMap::', salesFieldMap);

      setDayReportFieldsSalesEntry(salesFieldMap);
      setDayReportFieldsPOEntry(poFieldMap);
      setDayReportFieldsDepositEntry(depositFieldMap);
      setDayReportFieldsGasSalesEntry(gasSalesFieldMap);
    }
  }, [dayReportCategoryWiseFields]);

  const _loadSummary = async (reportId: number) => {
    const summaryData = await loadDayReportById(reportId);
    setSummaryData(summaryData);
    return summaryData;
  };

  const _loadSalesEntry = async (reportId: number) => {
    if (reportId) {
      const summaryData = await _loadSummary(reportId);
      if (summaryData) {
        const summaryTable = [] as IDayReportLineItem[];
        summaryTable.push({
          itemCode: 'totalCashReceived',
          lineItem: 'totalCashReceived',
          amount: summaryData.totalCashReceived,
        });
        summaryTable.push({
          itemCode: 'totalCashPO',
          lineItem: 'totalCashPO',
          amount: summaryData.totalCashPO,
        });
        summaryTable.push({
          itemCode: 'totalCashDeposit',
          lineItem: 'totalCashDeposit',
          amount: summaryData.totalCashDeposit,
        });
        summaryTable.push({
          itemCode: 'netCashReceived',
          lineItem: 'netCashReceived',
          amount: summaryData.netCashReceived,
        });
        summaryTable.push({
          itemCode: 'totalCashOver',
          lineItem: 'totalCashOver',
          amount: summaryData.totalCashOver,
        });

        setSummaryTableData({ items: summaryTable, totalPages: 1, currentPage: 1, totalItems: summaryTable.length });
      }

      if (summaryData?.storeId) {
        const store = await loadStoreById(summaryData?.storeId);
        setStoreData(store);
      }

      const resp = await loadDayReportEntry<IDayReportLineItem>('salesEntry', reportId);

      if (resp && resp.length > 0) {
        const salesItems = [...resp] as IDayReportLineItem[];

        console.log('Debug::dayReportFieldsSalesEntry::', dayReportFieldsSalesEntry);
        salesItems.sort(
          (a1, a2) =>
            ((dayReportFieldsSalesEntry ? dayReportFieldsSalesEntry[a1.itemCode.toLowerCase()]?.fieldOrder : 100) ||
              100) -
            ((dayReportFieldsSalesEntry ? dayReportFieldsSalesEntry[a2.itemCode.toLowerCase()]?.fieldOrder : 100) ||
              100),
        );
        BLANK_LINES_AFTER_SALES.forEach((code) => {
          const index = salesItems.findIndex((item) => item.itemCode === code);
          salesItems.splice(index + 1, 0, { itemCode: 'blank', lineItem: 'blank', amount: -1 });
        });
        setSalesData({
          totalItems: salesItems.length,
          items: salesItems,
          totalPages: 1,
          currentPage: 1,
        });
      }
    }
  };
  const _loadCashPOData = async (reportId: number) => {
    if (reportId) {
      const resp = await loadDayReportEntry<IDayReportLineItem>('cashPO', reportId);
      if (resp && resp.length > 0) {
        resp.sort(
          (a1, a2) =>
            ((dayReportFieldsPOEntry ? dayReportFieldsPOEntry[a1.itemCode.toLowerCase()]?.fieldOrder : 100) || 100) -
            ((dayReportFieldsPOEntry ? dayReportFieldsPOEntry[a2.itemCode.toLowerCase()]?.fieldOrder : 100) || 100),
        );
        setCashPOData({
          totalItems: resp.length,
          items: resp,
          totalPages: 1,
          currentPage: 1,
        });
      }
    }
  };
  const _loadCashDepositData = async (reportId: number) => {
    if (reportId) {
      const resp = await loadDayReportEntry<IDayReportLineItem>('cashDeposit', reportId);
      if (resp && resp.length > 0) {
        resp.sort(
          (a1, a2) =>
            ((dayReportFieldsDepositEntry ? dayReportFieldsDepositEntry[a1.itemCode.toLowerCase()]?.fieldOrder : 100) ||
              100) -
            ((dayReportFieldsDepositEntry ? dayReportFieldsDepositEntry[a2.itemCode.toLowerCase()]?.fieldOrder : 100) ||
              100),
        );
        setCashDepositData({
          totalItems: resp.length,
          items: resp,
          totalPages: 1,
          currentPage: 1,
        });
      }
    }
  };
  const _loadVoidData = async (reportId: number) => {
    if (reportId) {
      const resp = await loadDayReportEntry<IDayReportVoidLineItem>('voids', reportId);
      if (resp && resp.length > 0) {
        setVoidData({
          totalItems: resp.length,
          items: resp,
          totalPages: 1,
          currentPage: 1,
        });
      }
    }
  };
  const _loadGasData = async (reportId: number) => {
    if (reportId) {
      const resp = await loadGasReportEntry(reportId);
      if (resp && resp.dayReportId > 0) {
        // setGasReportData(resp);
        if (!resp.totalTaxGas) {
          resp.totalTaxGas = formatDecimalNumber(Number(resp.distTaxGas || 0) + Number(resp.stateTaxGas || 0));
        }
        if (!resp.totalTaxDsl) {
          resp.totalTaxDsl = formatDecimalNumber(Number(resp.distTaxDsl || 0) + Number(resp.stateTaxDsl || 0));
        }

        const gasItems = [] as IDayReportLineItem[];
        FORM_ITEM_CODES_GAS.map((code) => {
          gasItems.push({
            itemCode: code,
            lineItem: code,
            amount: resp[code as keyof IGasReport] as number,
          });
          if (BLANK_LINES_AFTER.indexOf(code) >= 0) {
            gasItems.push({ itemCode: 'blank', lineItem: 'blank', amount: -1 });
          }
        });

        setGasReportData({
          totalItems: gasItems.length,
          items: gasItems,
          totalPages: 1,
          currentPage: 1,
        });
      }
    }
  };

  const _loadReview = async (reportId: number) => {
    const data = await loadDayReportReviews(reportId);
    if (data) {
      setReviews({
        totalItems: data.length,
        items: data,
        currentPage: 1,
        totalPages: 1,
      });
    } else {
      setReviews(undefined);
    }
  };

  useEffect(() => {
    if (reportId && dayReportFieldsSalesEntry) {
      setTimeout(() => {
        _loadSalesEntry(reportId);
        _loadCashPOData(reportId);
        _loadCashDepositData(reportId);
        _loadVoidData(reportId);
        _loadGasData(reportId);
        _loadReview(reportId);
      }, 1000);
    }
  }, [reportId, dayReportFieldsSalesEntry]);

  const _renderSalesRow = (item: IDayReportLineItem, index: number): RowItem => {
    return _renderRow(
      item,
      index,
      dayReportFieldsSalesEntry && [item.itemCode.toLocaleLowerCase()]
        ? dayReportFieldsSalesEntry[item.itemCode.toLocaleLowerCase()]?.fieldDisplayName
        : item.itemCode,
    );
  };
  const _renderCashPORow = (item: IDayReportLineItem, index: number): RowItem => {
    return _renderRow(
      item,
      index,
      dayReportFieldsPOEntry && [item.itemCode.toLocaleLowerCase()]
        ? dayReportFieldsPOEntry[item.itemCode.toLocaleLowerCase()]?.fieldDisplayName
        : item.itemCode,
    );
  };
  const _renderCashDepositRow = (item: IDayReportLineItem, index: number): RowItem => {
    return _renderRow(
      item,
      index,
      dayReportFieldsDepositEntry && [item.itemCode.toLocaleLowerCase()]
        ? dayReportFieldsDepositEntry[item.itemCode.toLocaleLowerCase()]?.fieldDisplayName
        : item.itemCode,
    );
  };
  const _renderGasRow = (item: IDayReportLineItem, index: number): RowItem => {
    return _renderRow(
      item,
      index,
      dayReportFieldsGasSalesEntry && [item.itemCode.toLocaleLowerCase()]
        ? dayReportFieldsGasSalesEntry[item.itemCode.toLocaleLowerCase()]?.fieldDisplayName
        : item.itemCode,
    );
  };
  const _renderVoidRow = (item: IDayReportVoidLineItem, index: number): RowItem => {
    return {
      id: item.id ? item.id.toString() : item.txnNumber,
      index,
      data: [
        {
          label: item.txnNumber,
        },
        {
          label: item.amount + '',
          textAlign: 'right',
        },
      ],
    };
  };
  const _renderSummaryRow = (item: IDayReportLineItem, index: number): RowItem => {
    return {
      id: item.id ? item.id.toString() : item.itemCode,
      index,
      data: [
        {
          label: t('dayReport.summary.' + item.itemCode),
          isBold: BOLD_ITEMS_SUMMARY_TABLE.indexOf(item.itemCode) >= 0,
        },
        {
          label: item.amount + '',
          isBold: BOLD_ITEMS_SUMMARY_TABLE.indexOf(item.itemCode) >= 0,
          textAlign: 'right',
        },
      ],
    };
  };

  const _renderBlankRow = (index: number) => {
    return {
      id: index,
      index,
      data: [
        {
          label: '',
        },
        {
          label: '',
        },
      ],
    };
  };
  const _renderRow = (item: IDayReportLineItem, index: number, msgkey: string): RowItem => {
    if (item.itemCode === 'blank') {
      return _renderBlankRow(index);
    }
    return {
      id: item.id ? item.id.toString() : item.itemCode,
      index,
      data: [
        {
          label: 'miscItem' === item.itemCode ? item.lineItem || 'Misc Item' : msgkey,
          isBold: BOLD_ITEMS.indexOf(item.itemCode) >= 0,
        },
        {
          label: formatCurrency(item.amount, 'decimal', 2) + '',
          isBold: BOLD_ITEMS.indexOf(item.itemCode) >= 0,
          textAlign: 'right',
        },
      ],
    };
  };
  const _openFile = async (file: IReportExportData) => {
    if (file.documentUrl) {
      let presigned = preSignedUrlMap[file.documentUrl];
      if (!presigned) {
        presigned = await getPreSignedDocument(file.documentUrl);
        const temp = { ...preSignedUrlMap };
        temp[file.documentUrl] = presigned;
        setPreSignedUrlMap(temp);
      }
      if (presigned) {
        window.open(presigned, '_blank');
      }
    }
  };

  const _onRequestToModify = () => {
    setOpenReviewForm(true);
  };

  const _onCancelReview = () => {
    setComments('');
    setOpenReviewForm(false);
  };
  const _onSubmitReview = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!comments || comments.length < 10) {
      setReviewError('Please enter valid comments to continue. Min. length should be 10.');
      return false;
    }
    if (reportId) {
      const resp = await saveDayReportReviews({
        comments,
        reportId: reportId,
      } as IShiftReportReview);
      if (resp) {
        showSnackBar(SnackbarType.SUCCESS, t('success.updated'));
        setComments('');
        _loadReview(reportId);
        _loadSummary(reportId);
      }
    }
  };

  const _onRequestToApprove = async () => {
    if (summaryData) {
      const approved = await approveDayReport(summaryData?.id);
      if (approved) {
        showSnackBar(SnackbarType.SUCCESS, t('success.updated'));
        navigate('/reports');
      }
    }
  };

  const _handleCancel = () => {
    navigate('/reports');
  };

  return (
    <section>
      <h2 className="title">{t('page.dayReportDetails')}</h2>
      {isLoading ? <LoadingSpinner /> : null}
      <Paper
        style={{
          padding: '1rem 2rem',
          margin: '1rem 0rem',
        }}
      >
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} style={{ padding: '10px 0px' }}>
          <Typography variant="body1">
            <span style={globalStyles.deactivatedText}>Store:</span> #{storeData?.storeNumber}
          </Typography>
          <Typography variant="body1">
            <span style={globalStyles.deactivatedText}>Date: </span>
            {getDisplayDate(summaryData?.reportDate)}
          </Typography>
          <Typography variant="body1">
            <span style={globalStyles.deactivatedText}>Status: </span>
            {summaryData?.status}
          </Typography>
          <Typography variant="body1">
            <span style={globalStyles.deactivatedText}>Version: </span>
            {summaryData?.version}
          </Typography>
        </Box>
      </Paper>
      <Paper
        style={{
          padding: '1rem 2rem',
          margin: '1rem 0rem',
        }}
      >
        <Typography>Downloadables</Typography>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} style={{ padding: '10px 0px' }}>
          {summaryData && summaryData.pdfExportData && isArray(summaryData.pdfExportData)
            ? summaryData.pdfExportData?.map((file, index) => (
                <Box style={{ marginBottom: '6px' }} key={file.id}>
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    onClick={() => _openFile(file)}
                    style={{ backgroundColor: COLOR_ARRAY[0] }}
                    startIcon={<PictureAsPdfIcon />}
                  >
                    {file.documentName}
                  </Button>
                </Box>
              ))
            : null}
          {summaryData && summaryData.csvExportData && isArray(summaryData.csvExportData)
            ? summaryData.csvExportData?.map((file, index) => (
                <Box style={{ marginBottom: '6px' }} key={file.id}>
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    onClick={() => _openFile(file)}
                    style={{ backgroundColor: COLOR_ARRAY[1] }}
                    startIcon={<DescriptionIcon />}
                  >
                    {file.documentName}
                  </Button>
                </Box>
              ))
            : null}
        </Box>
      </Paper>
      <div style={{ marginTop: '20px' }}>
        <UITable<IDayReportLineItem>
          pagedData={salesData}
          title="Sales Entry"
          headers={[{ label: 'Item' }, { label: 'Amount', textAlign: 'right' }]}
          keyExtractor={(item) => (item.id ? 'se:' + item.id?.toString() : 'se:' + item.itemCode)}
          pageSize={10}
          loadData={undefined}
          extractRowItem={_renderSalesRow}
          viewOnly
          isLoading={loadingStatus?.salesEntry}
        />
        <UITable<IDayReportLineItem>
          pagedData={cashPOData}
          title="Cash PO"
          headers={[{ label: 'Item' }, { label: 'Amount', textAlign: 'right' }]}
          keyExtractor={(item) => (item.id ? 'po:' + item.id?.toString() : 'po:' + item.itemCode)}
          pageSize={10}
          loadData={undefined}
          extractRowItem={_renderCashPORow}
          viewOnly
          isLoading={loadingStatus?.cashPO}
        />
        <UITable<IDayReportLineItem>
          pagedData={cashDepositData}
          title="Cash Deposition"
          headers={[{ label: 'Item' }, { label: 'Amount', textAlign: 'right' }]}
          keyExtractor={(item) => (item.id ? 'de:' + item.id?.toString() : 'de:' + item.itemCode)}
          pageSize={10}
          loadData={undefined}
          extractRowItem={_renderCashDepositRow}
          viewOnly
          isLoading={loadingStatus?.cashDeposit}
        />
        <UITable<IDayReportVoidLineItem>
          pagedData={voidData}
          title="Voids"
          headers={[{ label: 'Transaction Number' }, { label: 'Amount', textAlign: 'right' }]}
          keyExtractor={(item) => (item.id ? 'vd:' + item.id?.toString() : 'vd:' + item.txnNumber)}
          pageSize={10}
          loadData={undefined}
          extractRowItem={_renderVoidRow}
          viewOnly
          isLoading={loadingStatus?.voids}
        />
        <UITable<IDayReportLineItem>
          pagedData={summaryTableData}
          title="Summary"
          headers={[{ label: 'Item' }, { label: 'Amount', textAlign: 'right' }]}
          keyExtractor={(item) => (item.id ? 'sm:' + item.id?.toString() : 'sm:' + item.itemCode)}
          pageSize={10}
          loadData={undefined}
          extractRowItem={_renderSummaryRow}
          viewOnly
          isLoading={loadingStatus?.report}
        />
        <UITable<IDayReportLineItem>
          pagedData={gasReportData}
          title="Gas Report"
          headers={[{ label: 'Item' }, { label: 'Amount' }]}
          keyExtractor={(item, index) => index + (item.id ? 'gs:' + item.id?.toString() : 'gs:' + item.itemCode)}
          pageSize={10}
          loadData={undefined}
          extractRowItem={_renderGasRow}
          viewOnly
          isLoading={loadingStatus?.gas}
        />
        {hasAccess(AccessKeys.REVIEW_DAY_REPORT) && summaryData?.status !== ShiftReportStatusEnum.APPROVED ? (
          <div>
            {!openReviewForm ? (
              <Box display="flex" flexWrap="wrap" gap={2} flexDirection="row" sx={{ margin: '40px 0px' }}>
                <Box>
                  <Button variant="contained" onClick={_onRequestToModify} disabled={isLoading}>
                    Request Modification
                  </Button>
                </Box>
                <Box>
                  <Button variant="contained" onClick={_onRequestToApprove} color="success" disabled={isLoading}>
                    Approve
                  </Button>
                </Box>
                <Button disabled={isLoading} variant="contained" onClick={_handleCancel} color="error">
                  Back
                </Button>
              </Box>
            ) : null}
            {openReviewForm ? (
              <Box
                component="form"
                sx={{
                  '& .MuiTextField-root': { m: 1.5 },
                  '& .select': { m: 1.5, width: '50ch' },
                  '& .selectLabel': { m: 1.5 },
                  '& .MuiButton-root': { m: 1.5 },
                  //   '& .MuiSelect-root': { m: 1, width: '30ch' },
                }}
                noValidate
                autoComplete="off"
              >
                <UICard title="">
                  <FormControl fullWidth>
                    <TextField
                      required
                      fullWidth
                      multiline
                      minRows={4}
                      id="outlined-required"
                      label="Reason"
                      value={comments || ''}
                      error={reviewError ? true : false}
                      helperText={reviewError || ''}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setComments(event.target.value);
                      }}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <Box flexDirection={'row'}>
                      <Button variant="contained" onClick={_onSubmitReview} style={{ marginTop: '30px' }}>
                        Submit
                      </Button>
                      <Button color="error" variant="contained" onClick={_onCancelReview} style={{ marginTop: '30px' }}>
                        Cancel
                      </Button>
                    </Box>
                  </FormControl>
                </UICard>
              </Box>
            ) : null}
          </div>
        ) : null}
        {reviews ? (
          <div style={{ margin: '10px' }}>
            <Typography component="span" variant="h6" sx={{ color: 'text.primary', display: 'inline' }}>
              Comments
            </Typography>
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {reviews.items.map((item) => (
                <React.Fragment key={item.id}>
                  <ListItem alignItems="flex-start">
                    {/* <ListItemAvatar>
                      <Avatar alt="Remy Sharp" src="/static/images/avatar/1.jpg" />
                       </ListItemAvatar> */}
                    <ListItemText
                      primary={
                        getReportDisplayDate(
                          item.submittedTime,
                          item.createdTime,
                          item.submittedTime,
                          DATE_FORMATS.DDMMYYYY_hhmmssa,
                        ) || ''
                      }
                      secondary={
                        <React.Fragment>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: 'text.primary', display: 'inline' }}
                          >
                            {item.comments}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                  <Divider variant="fullWidth" component="li" />
                </React.Fragment>
              ))}
            </List>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default DayReportDetails;
